/* global BigInt */
const LEVELS = ['TP1', 'TP2', 'TP3', 'SL'];
const LABEL_PATTERN = /\b(TP1|TP2|TP3|SL)\b/gi;
const SEGMENT_LEADING = /^[\s:=：>•·∙▪◦‣*]+/;
const NUMERIC_PATTERN = /^([+-]?(?:\d+(?:[.,]\d*)?|[.,]\d+))(?![\deE]|[.,]\d)([^\S\r\n]*%?)/;

function formatScaled(scaled, scale, trim = true) {
  let text = scaled.toString();
  if (scale === 0) {
    return text.replace(/^0+(?=\d)/, '');
  }
  if (text.length <= scale) {
    text = '0'.repeat(scale - text.length + 1) + text;
  }
  const splitAt = text.length - scale;
  const integer = text.slice(0, splitAt).replace(/^0+(?=\d)/, '') || '0';
  const rawFraction = text.slice(splitAt);
  const fraction = trim ? rawFraction.replace(/0+$/, '') : rawFraction;
  return fraction ? `${integer}.${fraction}` : integer;
}

export function parseDecimalPrice(value) {
  const normalized = String(value ?? '').trim().replace(',', '.');
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized)) {
    return null;
  }
  const unsigned = normalized.replace(/^[+-]/, '');
  const [integer, fraction = ''] = unsigned.split('.');
  const digits = `${integer || '0'}${fraction}`.replace(/^0+/, '') || '0';
  const unscaled = BigInt(digits);
  if (unscaled <= 0n || normalized.startsWith('-')) {
    return null;
  }
  const scale = fraction.length;
  return {
    unscaled,
    scale,
    normalized: formatScaled(unscaled, scale),
  };
}

export function adjustStopLoss(signalSl, direction) {
  const parsed = parseDecimalPrice(signalSl);
  if (!parsed) {
    return null;
  }
  const factor = direction === 'SHORT' ? 105n : 95n;
  const percent = direction === 'SHORT' ? 5 : -5;
  return {
    original: parsed.normalized,
    value: formatScaled(parsed.unscaled * factor, parsed.scale + 2),
    percent,
  };
}

export function normalizeDecimalInput(value) {
  return String(value ?? '').replace(/\s+/g, '').replace(/,/g, '.');
}

export function alignPriceToTick(value, tickSize, mode = 'DOWN') {
  const price = parseDecimalPrice(value);
  const tick = parseDecimalPrice(tickSize);
  if (!price || !tick) {
    return null;
  }
  const scale = Math.max(price.scale, tick.scale);
  const priceUnits = price.unscaled * (10n ** BigInt(scale - price.scale));
  const tickUnits = tick.unscaled * (10n ** BigInt(scale - tick.scale));
  const quotient = priceUnits / tickUnits;
  const remainder = priceUnits % tickUnits;
  const aligned = remainder === 0n
    ? priceUnits
    : (mode === 'UP' ? (quotient + 1n) * tickUnits : quotient * tickUnits);
  const resultScale = tick.scale;
  const resultUnits = aligned / (10n ** BigInt(scale - tick.scale));
  return {
    original: price.normalized,
    value: formatScaled(resultUnits, resultScale, false),
    changed: aligned !== priceUnits,
    tickSize: tick.normalized,
  };
}

export function alignStopLossLevel(original, direction, tickSize) {
  const adjusted = adjustStopLoss(original, direction);
  if (!adjusted) {
    return null;
  }
  const aligned = alignPriceToTick(
    adjusted.value,
    tickSize,
    direction === 'SHORT' ? 'UP' : 'DOWN',
  );
  if (!aligned) {
    return null;
  }
  return {
    ...aligned,
    original: adjusted.original,
    exactValue: adjusted.value,
    percent: adjusted.percent,
  };
}

export function alignSignalLevels(extraction, direction, tickSize) {
  const sl = alignStopLossLevel(extraction.slOriginal, direction, tickSize);
  const tpMode = direction === 'SHORT' ? 'DOWN' : 'UP';
  const tp1 = alignPriceToTick(extraction.tp1Original, tickSize, tpMode);
  const tp2 = alignPriceToTick(extraction.tp2Original, tickSize, tpMode);
  const tp3 = alignPriceToTick(extraction.tp3Original, tickSize, tpMode);
  if (!sl || !tp1 || !tp2 || !tp3) {
    return null;
  }
  return { sl, tp1, tp2, tp3 };
}

function parseLevelValue(segment) {
  const cleaned = segment.replace(SEGMENT_LEADING, '').trim();
  if (!cleaned) {
    return { error: 'valeur absente' };
  }
  const match = cleaned.match(NUMERIC_PATTERN);
  if (!match) {
    return { error: 'valeur non numérique' };
  }
  if (match[2] && match[2].includes('%')) {
    return { error: 'pourcentage de performance refusé comme prix' };
  }
  const parsed = parseDecimalPrice(match[1]);
  if (!parsed) {
    return { error: 'prix invalide ou non positif' };
  }
  return { value: parsed.normalized };
}

export function parseTelegramSignal(text) {
  if (typeof text !== 'string' || !text.trim()) {
    return { ok: false, error: 'Collez un signal Telegram avant extraction.' };
  }

  const values = new Map(LEVELS.map((level) => [level, []]));
  const lines = text.split(/\r?\n/);

  for (const line of lines) {
    const labels = [...line.matchAll(LABEL_PATTERN)];
    for (let index = 0; index < labels.length; index += 1) {
      const match = labels[index];
      const level = match[1].toUpperCase();
      const start = match.index + match[0].length;
      const end = index + 1 < labels.length ? labels[index + 1].index : line.length;
      const parsed = parseLevelValue(line.slice(start, end));
      if (parsed.error) {
        return { ok: false, error: `${level} : ${parsed.error}.` };
      }
      values.get(level).push(parsed.value);
    }
  }

  const missing = LEVELS.filter((level) => values.get(level).length === 0);
  if (missing.length > 0) {
    return { ok: false, error: `Niveaux manquants : ${missing.join(', ')}.` };
  }

  const levels = {};
  for (const level of LEVELS) {
    const distinct = [...new Set(values.get(level))];
    if (distinct.length > 1) {
      return { ok: false, error: `Valeurs contradictoires pour ${level}.` };
    }
    levels[level] = distinct[0];
  }

  return {
    ok: true,
    levels: {
      tp1: levels.TP1,
      tp2: levels.TP2,
      tp3: levels.TP3,
      sl: levels.SL,
    },
  };
}
